// The "3 shirts, the cheapest one is free" promo — ONE definition.
//
// The end date used to live in src/components/PromoBanner.jsx, which was
// the right instinct (one date) but the wrong place: the pricing engine
// needs it too, and src/lib/pricing.js must not import a React component.
// scripts/test-pricing.mjs runs that engine in plain Node — pulling React
// into it would break the script, and the engine is the one file in this
// codebase that has to stay boring.
//
// So the date lives here, PromoBanner re-exports it, and there is still
// exactly one of it. Change it here and the banner, the engine and the
// admin panel all move together.

export const PROMO_ENDS_AT = Date.parse('2026-12-01T05:00:00Z') // Nov 30, end of day Miami

export function isPromoActive(now = Date.now()) {
  return now < PROMO_ENDS_AT
}

// Which garments count as a "shirt", by id rather than by name.
//
// Two reasons. Names are editable in the admin catalog, so a rename would
// silently switch the promo off. And Blouse costs the same $8 as a Dress
// shirt — nothing but the id tells them apart, and a blouse must not count.
export const PROMO_SHIRT_ITEM_IDS = Object.freeze([
  '479d9686-6122-4b3c-acc6-a9bc56d09100', // Dress shirt
  '5ab1e801-fa8a-4104-9227-5211ddee66ff', // Polo
])

// Minimum shirts in ONE order before the cheapest becomes free.
export const PROMO_MIN_SHIRTS = 3

// Written to order_items.discount_reason. It is also the marker that tells
// the cleanup pass which discounts it owns: anything with another reason
// was put there by Sébastien and must never be touched.
export const FREE_SHIRT_REASON = 'Free shirt promo'

export const isPromoShirt = (itemId) => PROMO_SHIRT_ITEM_IDS.includes(itemId)

/**
 * Work out which order_items rows need writing so the free-shirt discount
 * matches `freeShirt`. Returns `[{ id, patch }]` — possibly empty.
 *
 * Pure on purpose: the caller does the I/O, this decides. Two jobs, and
 * the second is the one that gets forgotten:
 *
 *  1. put the discount on the chosen line
 *  2. REMOVE it from any line that still carries it and shouldn't — the
 *     customer drops from 3 shirts to 2, or a different line is now the
 *     cheapest. Miss this and they keep a discount they no longer qualify
 *     for, which is the visible half of the bug.
 *
 * Lines discounted for any other reason are never returned: a discount
 * Sébastien typed in outranks this.
 *
 * @param {Array} lines  priced lines (needs id, discount_*)
 * @param {{lineId: string, amountCents: number}|null} freeShirt
 */
export function reconcileFreeShirt(lines, freeShirt) {
  const out = []
  for (const l of lines || []) {
    if (!l?.id) continue
    const isChosen = freeShirt && l.id === freeShirt.lineId
    const carriesPromo = l.discount_reason === FREE_SHIRT_REASON

    if (isChosen) {
      const value = freeShirt.amountCents / 100
      const alreadyRight =
        carriesPromo
        && l.discount_type === 'amount'
        && Number(l.discount_value) === value
      if (alreadyRight) continue
      // Only ever overwrite an empty slot or our own discount.
      if (l.discount_type && !carriesPromo) continue
      out.push({
        id: l.id,
        patch: {
          discount_type: 'amount',
          discount_value: value,
          discount_reason: FREE_SHIRT_REASON,
        },
      })
      continue
    }

    if (carriesPromo) {
      out.push({
        id: l.id,
        patch: { discount_type: null, discount_value: null, discount_reason: null },
      })
    }
  }
  return out
}
